// hooks/usePayment.js
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/contextAuth";
import { 
  createLink,
  getSubs, 
  getSubsRzp,
  getInvoiceById,
  getInvoiceHtml,
  getPlans
} from "../api/payments";

const poll_interval = 6000;
const poll_timeout = 3 * 60 * 1000;

const usePayment = () => {
  const { user, userId, customer_id, getUser } = useAuth();
  const [payment, setPayment] = useState(null);
  const [paymentLink, setPaymentLink] = useState(null);
  const [plans, setPlans] = useState(null);
  const [subId, setSubId] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [loadingTestPlan, setLoadingTestPlan] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [loadingInvoiceLink, setLoadingInvoiceLink] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [isYearly, setIsYearly] = useState(true);
  const [error, setError] = useState(null);

  // refs for polling so we can cleanup properly & avoid duplicate polls
  const pollingRef = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const isPollingRef = useRef(false);

  const initData = async () => {
    if (!user) {
      resetState();
      return;
    }
    setLoadingPlans(true);
    try {
      const plansRes = await getPlans();    
      setPlans(plansRes || []);    
      try {
        await getInvoice(userId, customer_id);
      } catch (invErr) {
        console.error("Invoice load error (ignored):", invErr);
      }    
    } catch (err) {
      console.error("Error loading plans in initData:", err);
      setPlans([]);
    } finally {
      setLoadingPlans(false);
    }    

    try {
      const sub = await getSubs(userId);
      const subscription = Array.isArray(sub) ? sub[0] : sub;
      if (!subscription) {
        setSubId(null);
        setPayment(null);
        return;
      }

      const id = subscription.subscription_id || subscription.sub_id || subscription.id || null;
      setSubId(id);
      setPayment(Array.isArray(sub) ? sub : [subscription]);

      if (id && (!subscription.status || subscription.status === "created" || subscription.status === "pending")) {
        startSubsPolling(userId, id);
      } else if (subscription.status === "active" || subscription.status === "completed") {
        await getInvoice(userId, customer_id);
      }
    } catch (err) {
      console.error("Error fetching subscription in initData:", err);
    }
  };
  
  const getPaymentLink = async (userIdArg, plan_id, quantity) => {
    setLoadingPlanId(plan_id);
    setError(null);
  
    try {
      const response = await createLink(userIdArg, plan_id, quantity);  
      const subs = response?.data;
      const short_url = subs?.short_url;
      const id = subs?.id;
  
      // console.log('subs', subs);
      if (!id) {
        throw new Error("Invalid Razorpay subscription response");
      }
  
      setPaymentLink(short_url || null);
      setSubId(id);  
      if (short_url) {
        window.open(short_url, "_blank");
      }
  
      startSubsPolling(userIdArg, id);
      return subs;  
    } catch (err) {
      console.error("Payment Link Generation Failed:", err);
      setError(err);
      setLoadingPlanId(null);
      throw err;
    }
  };
  
  const stopPolling = useCallback(async () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    isPollingRef.current = false;  
    window.location.reload();
  }, []);

  const startSubsPolling = (userIdArg, sub_id) => {
    if (!userIdArg || !sub_id) return;
    if (isPollingRef.current) return;  
    // console.log("⏳ Starting subscription polling for:", sub_id);
  
    setLoadingPayment(true);
    isPollingRef.current = true;
  
    pollingRef.current = setInterval(async () => {
      try {
        const response = await getSubsRzp(userIdArg, sub_id);
        const razorpayData = response?.data;  
        if (!razorpayData) {
          console.warn("⚠️ No subscription data returned yet...");
          return;
        }
  
        const status = razorpayData?.status;
        // console.log("📡 Subscription Status:", status);
  
        if (status === "completed") {
          // console.log("🎉 Subscription completed, stopping polling.");  
          setPayment(razorpayData);
          setSubId(razorpayData.id);
          await getUser(userId);
          await getInvoice(userId);
          setLoadingPlanId(null);
          setLoadingPayment(false);
          stopPolling();
          return;
        }
  
        if (status === "active") {
          // console.log("🔄 Subscription active, stopping polling.");
          setPayment(razorpayData);
          setSubId(razorpayData.id);
          stopPolling();
          return;
        }
  
        if (["expired", "cancelled"].includes(status)) {
          // console.log("⚠️ Subscription ended:", status);
          stopPolling();
          return;
        }
  
      } catch (error) {
        console.error("❌ Polling Error:", error);
        stopPolling();
      }
    }, poll_interval);
  
    pollingTimeoutRef.current = setTimeout(() => {
      // console.log("⏳ Polling timeout reached — stopping.");
      stopPolling();
    }, poll_timeout);
  };

  const getPlansFunc = async () => {
    setLoadingPlans(true);
    setError(null);
    try {
      const response = await getPlans();
      setPlans(response);
      return response;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoadingPlans(false);
    }
  };

  const getInvoice = async (userIdArg) => {
    setLoadingInvoice(true);
  
    try {
      const response = await getInvoiceById(userIdArg);
      const invoices = response?.data || [];
  
      // STEP 1 — Merge items + attach our invoice_id
      const mergedItems = invoices.flatMap(inv => {
        const items = inv?.invoice_details?.items || [];
        const myInvoiceId = inv.invoice_id;
        return items.map(item => ({
          ...item,
          invoice_id: myInvoiceId,
        }));
      });
  
      // STEP 2 — SORT by created_at DESC (latest at top)
      mergedItems.sort((a, b) => b.created_at - a.created_at);
  
      setInvoice(mergedItems);
      return mergedItems;
  
    } catch (error) {
      console.error("Invoice fetch error:", error);
      throw error;
  
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleViewInvoiceHtml = async (invoice_id) => {
    try {
      const html = await getInvoiceHtml(
        user.id,
        invoice_id
      );
      
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (error) {
      console.error("View Invoice Error:", error);
    }
  };
  
  useEffect(() => {
    const fetchPlans = async () => {
      setLoadingPlans(true);
      try {
        const res = await getPlans();
        setPlans(res);
      } catch (e) {
        console.error("Error loading plans", e);
        setPlans([]);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  const resetState = () => {
    stopPolling();
    setPayment(null);
    setPaymentLink(null);
    setPlans(null);
    setSubId(null);    
    setLoadingPlans(false);
    setLoadingPlanId(null);
    setLoadingTestPlan(false);
    setLoadingPayment(false);
    setLoadingInvoice(false);
    setLoadingInvoiceLink(false);
    setInvoice(null);
    setError(null);
  };

  return {
    error, setError,
    
    payment, setPayment,
    paymentLink, setPaymentLink,
    plans, setPlans,
    subId, setSubId,

    loadingPlans, setLoadingPlans,
    loadingPlanId, setLoadingPlanId,
    loadingTestPlan, setLoadingTestPlan,
    loadingPayment, setLoadingPayment,
    loadingInvoice, setLoadingInvoice,
    loadingInvoiceLink, setLoadingInvoiceLink,
    invoice, setInvoice,
    isYearly, setIsYearly,

    getPaymentLink,
    getPlansFunc,
    getInvoice,
    handleViewInvoiceHtml, 
    
    initData,
    resetState,
  };
};

export default usePayment;
